/* eslint-disable class-methods-use-this */
import {defineMessages} from 'react-intl';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  merge,
  Subscription,
  take,
} from 'rxjs';
import {defer} from '@sail/utils';
import isEqual from 'fast-deep-equal/react';
import {v4 as uuid} from 'uuid';
import {
  AbstractClient,
  Client,
  ClientResponse,
  ClientUrls,
  Conversation,
  ConversationAction,
  ConversationActionParams,
  ConversationChannel,
  ConversationParticipant,
  ConversationSyncStorageMode,
  GetConversationMessagesParams,
  GetConversationMessagesResponse,
  InProgressParams,
  InProgressResponse,
  LeaveConversationParams,
  ObservabilityConfig,
  SelectOptionAction,
  StartConversationParams,
  StartConversationResponse,
  GetConversationParticipantsParams,
  GetConversationParticipantsResponse,
  SendFeedbackParams,
  SendConversationMessageParams,
  SendConversationTypingParams,
  StreamTextParams,
  StreamTextResponse,
  ConversationLogItemMessageAdded,
  ButtonOptionsConversationMessage,
  ConversationArticle,
  ConversationStatus,
  TriggerParams,
  ConversationLogItem,
  ConversationMessage,
  RequestAction,
  InlineDataConversationMessage,
  ClientErrorResponse,
  ConversationContext,
  SelectOptionsConversationMessage,
} from '@stripe-internal/conversations-client';
import {
  UniversalHelpClient,
  UniversalHelpUser,
} from '@stripe-internal/universal-help-conversations-client';
import {InlineDataEventBus} from '@stripe-internal/inline-data/dist/InlineDataEventBusProvider';
import {devLog} from '../../helpers/dev';
import {METRIC} from './clientMetrics';
import {FeatureFlags} from '../../../generated/types';
import {Api} from '../ApiProvider/api/api';
import {isAssistantTriggerPoint} from '../../helpers/triggerPoints';
import {shouldConversationSendPageContext} from '../../Screens/ChatScreen/conversationUtils';

export const messages = defineMessages({
  closeChatConfirmation: {
    id: 'support_conversations_client.universal_help_client.closeChatConfirmation.closeChatDescription',
    description:
      'Instruction provided when user intends to close the chat window',
    defaultMessage: 'Are you sure you want to end this chat?',
  },
  closeChatButtonText: {
    id: 'support_conversations_client.universal_help_client.closeChatConfirmation.closeChatButtonText',
    description:
      'Text shown for the close chat button when closing support chat window',
    defaultMessage: 'End the chat',
  },
  chatEndedMessage: {
    id: 'support_conversations_client.universal_help_client.chatEndedMessage',
    description:
      'Message to show to prompt user to close chat widget to start new chat',
    defaultMessage:
      'For further assistance, please close the window and start a new chat.',
  },
  newConversationPrompt: {
    id: 'support_conversations_client.universal_help_client.newConversationPrompt',
    description:
      'Message to show to prompt user to enter a message to start a chat',
    defaultMessage: 'Send a message to let us know how we can help.',
  },
  waitingForAgent: {
    id: 'support_conversations_client.universal_help_client.waitingForAgent',
    description:
      'Message to show to prompt user to enter a message to start a chat',
    defaultMessage: 'Waiting for an agent to join.',
  },
  transitionToAgent: {
    id: 'support_conversations_client.universal_help_client.transitionToAgent',
    description:
      'Message to show to prompt user when transitioning chat to an agent',
    defaultMessage: 'Connecting you with an agent.',
  },
  transitionToAgentDotCom: {
    id: 'support_conversations_client.universal_help_client.transitionToAgentDotCom',
    description:
      'Message to show to prompt user when transitioning chat to an agent for dot com',
    defaultMessage: 'Adding an agent to the chat.',
  },
  transitionToAgentV2: {
    id: 'support_conversations_client.universal_help_client.transitionToAgentV2',
    description:
      'Message to show to prompt user when transitioning chat to an agent',
    defaultMessage: "Hang tight, we're connecting you to a support agent.",
  },
});

export const UHLiveChannelsClientType = 'universal_help_live_channels';
const METRIC_TAGS = {client_type: UHLiveChannelsClientType};
const ERROR_TAGS = METRIC_TAGS;

const INTERNAL_TEST_STRING = /^\s*stripe\s+internal\s+test\s*$/i;
const INTERNAL_SYNTHETIC_TEST_STRING =
  /^\s*stripe\s+internal\s+synthetic\s+test\s*$/i;

const INTERRUPTABLE_INLINE_DATA_NAMES = ['live_answers_response'];

export interface UniversalHelpConversationContext {
  articlesVisited?: Record<string, ConversationArticle>;
}

type StartInProgressConversationParams = StartConversationParams & {
  inProgressResponse?: InProgressResponse;
};

export class UHLiveChannelsClient extends AbstractClient implements Client {
  private featureFlags: FeatureFlags;

  private api: Api;

  private uhClient: UniversalHelpClient;

  private chatClient: Client;

  private inlineDataEventBus: InlineDataEventBus;

  private requestAction: (action: RequestAction) => Promise<ClientResponse>;

  private conversation?: Conversation | null;

  private conversationSub?: Subscription | null;

  private currentChannel: ConversationChannel = 'bot';

  private clientStatusSub?: Subscription | null;

  private nextTriggerQuery?: string;

  // Track if the initial greeting has been shown across conversation instances
  private initialGreetingShown = false;

  private stuckConversationRequests = new WeakMap<Conversation, number>();

  constructor({
    featureFlags,
    urls,
    observability,
    api,
    uhClient,
    chatClient,
    inlineDataEventBus,
    requestAction,
  }: {
    featureFlags: FeatureFlags;
    urls?: Partial<ClientUrls>;
    observability?: ObservabilityConfig;
    api: Api;
    uhClient: UniversalHelpClient;
    chatClient: Client;
    inlineDataEventBus: InlineDataEventBus;
    requestAction: (action: RequestAction) => Promise<ClientResponse>;
  }) {
    super({clientFetch: api.fetch, urls, observability});

    this.featureFlags = featureFlags;

    this.api = api;

    this.inlineDataEventBus = inlineDataEventBus;

    this.requestAction = requestAction;

    this.uhClient = uhClient;
    this.chatClient = chatClient;

    const clientExceptions = merge(
      this.uhClient
        .eventsObservable()
        .pipe(filter((e) => e.name === 'client-exception')),
      this.chatClient
        .eventsObservable()
        .pipe(filter((e) => e.name === 'client-exception')),
    );

    const conversationExceptions = merge(
      this.uhClient
        .conversationEventsObservable()
        .pipe(filter((e) => e.name === 'conversation-exception')),
      this.chatClient
        .conversationEventsObservable()
        .pipe(filter((e) => e.name === 'conversation-exception')),
    );

    clientExceptions.subscribe((e) => this.emitEvent(e));
    conversationExceptions.subscribe((e) => this.emitConversationEvent(e));

    this.subscribeToClientStatus(this.uhClient);
  }

  async startConversation(
    params: StartInProgressConversationParams,
  ): Promise<StartConversationResponse> {
    try {
      if (this.conversation) {
        if (
          params.context?.channel &&
          params.context.channel !== this.conversation.context.channel
        ) {
          throw new Error('Conversation already started');
        }
        if (
          params.conversationId &&
          params.conversationId !== this.conversation.id
        ) {
          throw new Error('Conversation already started');
        }
        // if this gets called twice in succession, and we've already
        // started a conversation, just return it.
        return {code: 200, data: {conversation: this.conversation}};
      }

      const {
        conversationId: paramsConversationId,
        participant,
        context,
      } = params;
      if (!participant) {
        throw new Error('Participant is required');
      }

      let {inProgressResponse} = params;
      if (!inProgressResponse) {
        inProgressResponse = await this.inProgress(params);
      }

      let channelConversationId: string | undefined;
      if (context) {
        channelConversationId = context[`${context.channel}_conversationId`] as
          | string
          | undefined;
      }
      let conversationId = paramsConversationId;

      if (!('error' in inProgressResponse)) {
        const {inProgress, channel} = inProgressResponse.data;
        if (inProgress) {
          conversationId = inProgressResponse.data.conversationId;
          if (channel === 'bot') {
            channelConversationId = conversationId;
            return this.startUHConversation({
              ...params,
              conversationId,
              channelConversationId,
              participant,
            });
          }

          if (channel === 'chat' && conversationId) {
            return this.resumeChatConversation({
              ...params,
              conversationId,
              channelConversationId,
              participant,
              fetch: true,
            });
          }

          if (channel === 'request_a_call') {
            return this.startRACConversation({
              ...params,
              conversationId,
              participant,
            });
          }
        }
      }

      // If there is no conversation in progress (which is handled above), we need to start a new UH conversation
      // so ignore the current conversationId
      return this.startUHConversation({
        ...params,
        conversationId: undefined,
        participant,
      });
    } catch (e) {
      const clientErrorResponse = await this.reportAndEmitException(
        `exception starting conversation: ${(e as Error).message}`,
      );
      return clientErrorResponse;
    }
  }

  private async startUHConversation(
    params: StartConversationParams & {
      conversationId?: string;
      channelConversationId?: string;
      participant: ConversationParticipant;
    },
  ): Promise<StartConversationResponse> {
    if (this.conversation) {
      throw new Error('Conversation already started');
    }

    const {context, conversationId, channelConversationId, participant} =
      params;

    if (context?.placeholderConversation) {
      return this.startPlaceholderConversation(params);
    }

    if (
      context?.entryPoint === 'dot_com' ||
      (!!context?.triggerParams &&
        (context?.triggerParams.initialQuery ||
          isAssistantTriggerPoint(context.triggerParams.triggerPoint)))
    ) {
      const result = await this.requestAction({
        name: 'request-action',
        waitForResponse: true,
        action: {
          name: 'get-page-context',
        },
      });

      if (result.data) {
        params.data = {
          ...params.data,
          ...result.data,
        };
      }
    }

    const uhResp = await this.startDisconnectedUHConversation({
      ...params,
      conversationId: channelConversationId,
    });

    if ('error' in uhResp) {
      return uhResp;
    }
    this.currentChannel = 'bot';

    const {conversation: uhConversation} = uhResp.data;

    let {enabledInputModes} = uhConversation.context;
    if (context?.entryPoint === 'dot_com') {
      enabledInputModes = [];
    }

    const conversation = new Conversation(this, {
      id: conversationId || uhConversation.id,
      user: participant,
      status: uhConversation.status,
      syncStorage: this.uhClient.syncStorageMode,
      messages: {...uhConversation.messages},
      participants: {
        ...uhConversation.participants,
        [participant.id]: participant,
      },
      context: {
        ...uhConversation.context,
        enabledInputModes,
        channel: 'bot',
      },
    });
    this.conversation = conversation;

    this.listenForConversationEvents(conversation);

    // Don't need to fetch latest, since we get the latest as part of startConversation
    conversation.connect({fetch: false});

    return {code: 200, data: {conversation}};
  }

  // this method is expected to only be used when resuming an existing conversation
  // that started as a UH conversation. That's why it expects a defined `conversationId`.
  private async resumeChatConversation(
    params: StartConversationParams & {
      conversationId: string;
      channelConversationId?: string;
      participant: ConversationParticipant;
    },
  ): Promise<StartConversationResponse> {
    if (this.conversation) {
      throw new Error('Conversation already started');
    }

    const {conversationId, channelConversationId, participant, fetch} = params;

    const chatResp = await this.startDisconnectedChatConversation({
      ...params,
      conversationId: channelConversationId,
      uhConversationId: conversationId,
    });

    if ('error' in chatResp) {
      return chatResp;
    }
    this.currentChannel = 'chat';

    const {conversation: chatConversation} = chatResp.data;

    const providerArgs = await this.getProviderArgs();

    const conversation = new Conversation(this, {
      id: conversationId,
      user: participant,
      status: chatConversation.status,
      syncStorage: this.chatClient.syncStorageMode,
      messages: {...chatConversation.messages},
      participants: {
        ...chatConversation.participants,
        [participant.id]: participant,
      },
      context: {
        ...chatConversation.context,
        channel: 'chat',
        requestedChannel: 'chat',
        intent: 'contact_support',
        enabledInputModes: undefined,
        providerArgs,
      },
    });
    this.conversation = conversation;

    this.listenForConversationEvents(conversation);

    // Intentionally not awaiting here; let the fetch happen async if passed
    conversation.connect({fetch});

    return {code: 200, data: {conversation}};
  }

  private async startRACConversation(
    params: StartConversationParams & {
      conversationId?: string;
      participant: ConversationParticipant;
    },
  ): Promise<StartConversationResponse> {
    this.currentChannel = 'request_a_call';
    const {conversationId, participant, context} = params;

    // for RAC + email, we don't actually want to start a new
    // conversation. we just want to return the existing conversation.
    // at this point, the UH conversation would have ended. we
    // would just be displaying the conversation history.
    // the UI should route this conversation to the RAC screen
    const conversation = new Conversation(this, {
      id: conversationId || uuid(),
      user: participant,
      syncStorage: this.uhClient.syncStorageMode,
      context: {
        ...context,
        channel: 'request_a_call',
        requestedChannel: 'request_a_call',
        intent: 'contact_support',
        enabledInputModes: undefined,
      },
    });

    this.conversation = conversation;

    this.listenForConversationEvents(conversation);

    conversation.connect({fetch: false});

    return {code: 200, data: {conversation}};
  }

  private async getProviderArgs() {
    const response = await this.requestAction({
      name: 'request-action',
      waitForResponse: true,
      action: {
        name: 'get-provider-args',
      },
    });
    if (response.data) {
      return (response.data as Record<string, unknown>).providerArgs || {};
    }
    return {};
  }

  private async startDisconnectedUHConversation(
    params: StartConversationParams,
  ): Promise<StartConversationResponse> {
    return this.uhClient.startConversation({
      ...params,
      connect: false,
      syncStorage: false,
    });
  }

  private async startDisconnectedChatConversation(
    params: StartConversationParams,
  ): Promise<StartConversationResponse> {
    return this.chatClient.startConversation({
      ...params,
      connect: false,
      fetch: false,
      syncStorage: false,
    });
  }

  private listenForConversationEvents(conversation: Conversation) {
    this.conversationSub?.unsubscribe();

    const sub = new Subscription();
    this.conversationSub = sub;

    sub.add(
      this.uhClient.conversationEventsObservable().subscribe((event) => {
        if (
          this.conversation &&
          event.conversationId === this.uhConversationId
        ) {
          this.emitConversationEvent({
            ...event,
            conversationId: this.conversation.id,
          });
        }
      }),
    );

    sub.add(
      this.chatClient.conversationEventsObservable().subscribe((event) => {
        if (
          this.conversation &&
          event.conversationId === this.chatConversationId
        ) {
          this.emitConversationEvent({
            ...event,
            conversationId: this.conversation.id,
          });
        }
      }),
    );

    const events$ = conversation.eventsObservable();
    const context$ = conversation.contextObservable();

    const conversationEnded$ = events$.pipe(
      filter((e) => e.name === 'conversation-ended'),
      take(1),
    );

    const restartedConversation$ = context$.pipe(
      distinctUntilChanged((prev, cur) => prev.intent === cur.intent),
      filter((context) => context.intent === 'restart_conversation'),
    );

    const channelChanged$ = context$.pipe(
      distinctUntilChanged((prev, cur) => prev.channel === cur.channel),
    );
    const transitionedToChat$ = channelChanged$.pipe(
      filter((context) => context.channel === 'chat'),
    );

    const transitionedToRAC$ = channelChanged$.pipe(
      filter((context) => context.channel === 'request_a_call'),
    );

    const transitionedToEmail$ = channelChanged$.pipe(
      filter((context) => context.channel === 'email'),
    );

    sub.add(
      transitionedToChat$.subscribe(() => {
        this.transitionToChat();
      }),
    );

    sub.add(
      transitionedToRAC$.subscribe(() => {
        this.transitionToRAC();
      }),
    );

    if (this.featureFlags.universalHelpUseInlineRacEmailForm) {
      sub.add(
        transitionedToEmail$.subscribe(() => {
          this.transitionToEmail();
        }),
      );
    }

    sub.add(
      restartedConversation$.subscribe(() => {
        this.restartConversation();
      }),
    );

    sub.add(
      conversationEnded$.subscribe(() => {
        if (!conversation.context.disableCleanupOnEnd) {
          conversation.disconnect();
        }
        this.appendChatEndedMetaEvent();

        // Don't clear conversation immediately if we're waiting for animations.
        // clearCurrentConversation will be called after animations complete and we show the chat ended message
        if (
          conversation.context.audience === 'dot_com' &&
          !conversation.context.supressChatEndedNotice
        ) {
          return;
        }
        this.clearCurrentConversation();
      }),
    );
  }

  async inProgress(params: InProgressParams): Promise<InProgressResponse> {
    if (this.featureFlags.racInProgressV2) {
      return this.inProgressV2(params);
    }

    const {conversationId, context} = params;

    let channelConversationId: string | undefined;
    if (context?.channel) {
      // params will include any ConversationContext, so check for
      // channel-specific conversation id
      channelConversationId = context[`${context.channel}_conversationId`] as
        | string
        | undefined;
    }

    const channelParams = {
      ...params,
      // when we check individual channels, use the channel-specific id
      conversationId: channelConversationId || conversationId,
    };

    // when we're returning a response from this call, if we're returning
    // inProgress for a given conversation id, we should return that id
    const responseConversationId = conversationId || channelConversationId;

    try {
      if (context?.channel === 'request_a_call' || !context?.channel) {
        const racResp = await this.api.inProgressRequestACall(
          context?.entryPoint ?? '',
          context?.audience ?? '',
        );

        if ('error' in racResp) {
          return racResp;
        }

        return {
          code: 200,
          data: {
            ...racResp.data,
            channel: 'request_a_call',
            conversationId: responseConversationId,
          },
        };
      }

      if (context?.channel === 'chat') {
        const chatResp = await this.chatClient.inProgress(channelParams);

        if ('error' in chatResp) {
          return chatResp;
        }

        return {
          code: 200,
          data: {
            ...chatResp.data,
            channel: 'chat',
            conversationId: responseConversationId,
          },
        };
      }

      const uhResp = await this.uhClient.inProgress(channelParams);

      if ('error' in uhResp) {
        return uhResp;
      }

      const {conversationId: uhResponseConversationId} = uhResp.data;

      return {
        code: 200,
        data: {
          ...uhResp.data,
          channel: 'bot',
          conversationId: uhResponseConversationId ?? responseConversationId,
        },
      };
    } catch (e) {
      return this.error(500, (e as Error).message, ERROR_TAGS);
    }
  }

  async inProgressV2(params: InProgressParams): Promise<InProgressResponse> {
    const {conversationId, context} = params;

    let channelConversationId: string | undefined;
    if (context?.channel) {
      // params will include any ConversationContext, so check for
      // channel-specific conversation id
      channelConversationId = context[`${context.channel}_conversationId`] as
        | string
        | undefined;
    }

    const channelParams = {
      ...params,
      // when we check individual channels, use the channel-specific id
      conversationId: channelConversationId || conversationId,
    };

    // when we're returning a response from this call, if we're returning
    // inProgress for a given conversation id, we should return that id
    const responseConversationId = conversationId || channelConversationId;

    try {
      const respPromises: Promise<InProgressResponse>[] = [];
      if (context?.channel === 'chat') {
        respPromises.push(
          new Promise<InProgressResponse>((resolve) => {
            this.chatClient.inProgress(channelParams).then((chatResp) => {
              if ('error' in chatResp) {
                resolve(chatResp);
                return;
              }

              resolve({
                code: 200,
                data: {
                  ...chatResp.data,
                  channel: 'chat',
                  conversationId: responseConversationId,
                },
              });
            });
          }),
        );
      }
      respPromises.push(
        new Promise<InProgressResponse>((resolve) => {
          this.api
            .inProgressRequestACall(
              context?.entryPoint ?? '',
              context?.audience ?? '',
            )
            .then((racResp) => {
              if ('error' in racResp) {
                resolve(racResp);
                return;
              }

              resolve({
                code: 200,
                data: {
                  ...racResp.data,
                  channel: 'request_a_call',
                  conversationId: responseConversationId,
                },
              });
            });
        }),
      );
      respPromises.push(
        new Promise<InProgressResponse>((resolve) => {
          this.uhClient.inProgress(channelParams).then((uhResp) => {
            if ('error' in uhResp) {
              resolve(uhResp);
              return;
            }

            const {conversationId: uhResponseConversationId} = uhResp.data;

            resolve({
              code: 200,
              data: {
                ...uhResp.data,
                channel: 'bot',
                conversationId:
                  uhResponseConversationId ?? responseConversationId,
              },
            });
          });
        }),
      );

      const responses = await Promise.all(respPromises);

      return this.inProgressResponseForRequest(responses, params);
    } catch (e) {
      return this.error(500, (e as Error).message, ERROR_TAGS);
    }
  }

  private async transitionToChat() {
    try {
      if (!this.conversation) {
        throw new Error('Conversation not started');
      }

      if (this.currentChannel === 'chat') {
        return;
      }

      this.subscribeToClientStatus(this.chatClient);

      this.currentChannel = 'chat';

      this.appendTransitionToAgentMetaEvent();

      await this.conversation?.disconnect();
      await this.fetchLatestAndLeaveUHConversation();

      const providerArgs = await this.getProviderArgs();

      const resp = await this.startDisconnectedChatConversation({
        conversationId: this.chatConversationId,
        participant: this.conversation.user,
        context: {...this.conversation.context, providerArgs},
        uhConversationId: this.uhConversationId,
      });

      if ('error' in resp) {
        this.incrementAndTrack(METRIC.transition_to_chat_error, {
          code: resp.code,
        });
        this.clearCurrentConversation();
        throw new Error(resp.error);
      }

      const {conversation: chatConversation} = resp.data;

      this.conversation.setContextValues({
        ...chatConversation.context,
        channel: 'chat',
        requestedChannel: 'chat',
        intent: 'contact_support',
      });

      await this.conversation.connect({fetch: true});

      this.conversation.setContextValue('enabledInputModes', undefined);

      // these test + synthetic messages use the chat client directly, since otherwise
      // they'd get swallowed and not sent since we intercept them in this client
      if (this.conversation.context.dev) {
        devLog('Initiating internal test chat');
        await this.chatClient.sendConversationMessage({
          conversationId: this.chatConversationId as string,
          message: {
            ...this.conversation.buildTextMessage('Stripe Internal Test'),
            nonce: 'ignore-internal-test-message',
          },
        });
      } else if (this.conversation.context.synthetic) {
        devLog('Initiating internal synthetic test chat');
        await this.chatClient.sendConversationMessage({
          conversationId: this.chatConversationId as string,
          message: {
            ...this.conversation.buildTextMessage(
              'Stripe Internal Synthetic Test',
            ),
            nonce: 'ignore-internal-synthetic-test-message',
          },
        });
      }

      await this.conversation.sendMessage({
        ...this.buildUHConversationSummaryMessage(),
        nonce: 'ignore-initial-uh-chat-context-message',
      });

      this.incrementAndTrack(METRIC.transition_to_chat_success);
    } catch (e) {
      this.reportAndEmitException(
        `exception transitioning conversation to chat: ${(e as Error).message}`,
        false,
      ); // Not a 5xx error
      this.incrementAndTrack(METRIC.transition_to_chat_error);
    }
  }

  private appendTransitionToAgentMetaEvent() {
    if (!this.conversation) {
      return;
    }

    if (
      this.featureFlags.universalHelpUseNewDesign &&
      this.featureFlags.enableUniversalHelp
    ) {
      this.conversation.log.appendMetaEvent({
        id: 'transition-to-agent',
        message: messages.transitionToAgentV2,
        style: 'body',
      });
    } else if (this.conversation?.context.audience === 'dot_com') {
      this.conversation.log.appendMetaEvent({
        id: 'transition-to-agent',
        message: messages.transitionToAgentDotCom,
      });
    } else {
      this.conversation.log.appendMetaEvent({
        id: 'transition-to-agent',
        message: messages.transitionToAgent,
      });
    }
  }

  private appendChatEndedMetaEvent() {
    if (!this.conversation) {
      return;
    }

    if (this.conversation.context.supressChatEndedNotice) {
      return;
    }

    if (
      this.featureFlags.universalHelpUseNewDesign &&
      this.featureFlags.enableUniversalHelp
    ) {
      return;
    }

    if (this.conversation.context.audience === 'dot_com') {
      this.setContextToWaitForAnimations();
      return;
    }

    this.conversation.log.appendMetaEvent({
      id: 'chat-ended-notice',
      message: messages.chatEndedMessage,
    });
  }

  private setContextToWaitForAnimations() {
    if (!this.conversation) {
      return;
    }

    const currentLogItems = this.conversation.log.get();

    // Set a flag to indicate that we're waiting for animations to complete and
    // store the target items length - this is the length we need to reach for animations to be complete.
    // This prevents the target from moving if new items are added after the conversation ends
    this.conversation.setContextValues({
      waitingForAnimationsToComplete: true,
      animationTargetLength: currentLogItems.length,
    });

    // The animation system will signal completion via the animations-complete action
  }

  private showChatEndedMessage() {
    if (!this.conversation) {
      return;
    }

    this.conversation.clearContextValues([
      'waitingForAnimationsToComplete',
      'animationTargetLength',
    ]);

    // Skip showing the message for dot_com audiences
    if (this.conversation.context.audience === 'dot_com') {
      return;
    }

    this.conversation.log.appendMetaEvent({
      id: 'chat-ended-notice',
      message: messages.chatEndedMessage,
    });
  }

  private async transitionToRAC() {
    try {
      if (!this.conversation) {
        throw new Error('Conversation not started');
      }

      if (this.currentChannel === 'request_a_call') {
        return;
      }
      this.currentChannel = 'request_a_call';

      await this.conversation?.disconnect();
      await this.fetchLatestAndLeaveUHConversation();

      // we just ended the UH conversation. we want to re-subscribe,
      // but not re-fetch since there's no conversation to fetch
      await this.conversation?.connect({fetch: false});

      this.conversation?.setContextValues({
        channel: 'request_a_call',
        requestedChannel: 'request_a_call',
        intent: 'contact_support',
        enabledInputModes: [],
      });

      this.incrementAndTrack(METRIC.transition_to_rac_success);
    } catch (e) {
      this.reportAndEmitException(
        `exception transitioning conversation to rac: ${(e as Error).message}`,
        false,
      ); // Not a 5xx error
      this.incrementAndTrack(METRIC.transition_to_rac_error);
    }
  }

  private async transitionToEmail() {
    try {
      if (!this.conversation) {
        throw new Error('Conversation not started');
      }

      if (this.currentChannel === 'email') {
        return;
      }
      this.currentChannel = 'email';

      this.conversation.setContextValue('supressChatEndedNotice', true);
      this.conversation.leave();

      this.incrementAndTrack(METRIC.transition_to_email_success);
    } catch (e) {
      this.reportAndEmitException(
        `exception transitioning conversation to email: ${
          (e as Error).message
        }`,
        false,
      ); // Not a 5xx error
      this.incrementAndTrack(METRIC.transition_to_email_error);
    }
  }

  private async fetchLatestAndLeaveUHConversation() {
    this.conversation?.clearTypers();

    const uhConversationEnded = firstValueFrom(
      this.uhClient
        .conversationEventsObservable()
        .pipe(
          filter(
            (e) =>
              e.name === 'conversation-updated' &&
              e.status === ConversationStatus.Inactive,
          ),
        ),
    );

    // we make sure we fetch the latest messages before we leave + disconnect
    const latestUHMessagesResp = await this.uhClient.getConversationMessages({
      conversationId: this.uhConversationId as string,
    });

    if (!this.uhConversationId) {
      return;
    }

    await this.uhClient.leaveConversation({
      conversationId: this.uhConversationId as string,
      participant: UniversalHelpUser,
    });

    // we emit all the latest UH messages here, since they're emitted async
    // and might get emitted while the conversation is disconnect and leaving
    // the UH conversation
    if (!('error' in latestUHMessagesResp)) {
      const {messages: latestUHMessages} = latestUHMessagesResp.data;
      Object.values(latestUHMessages).forEach((message) => {
        this.conversation?.handleEventFromClient(
          this.newConversationEvent({
            name: 'conversation-message-added',
            conversationId: this.conversation?.id as string,
            participantId: message.participant.id,
            messageId: message.id,
            message,
          }),
        );
      });
    }

    await uhConversationEnded;
  }

  private async restartConversation() {
    try {
      if (!this.conversation) {
        throw new Error('Conversation not started');
      }

      if (this.currentChannel !== 'bot') {
        throw new Error('Can only restart bot conversations');
      }

      this.conversation.clearTypers();

      await this.conversation.disconnect();
      await this.uhClient.leaveConversation({
        conversationId: this.conversation.id,
        participant: UniversalHelpUser,
      });

      const resp = await this.startDisconnectedUHConversation({
        participant: this.conversation.user,
        context: {...this.conversation.context},
      });

      if ('error' in resp) {
        throw new Error(resp.error);
      }

      this.conversation.setContextValues({
        ...resp.data.conversation.context,
      });

      await this.conversation.connect({fetch: true});
    } catch (e) {
      this.reportAndEmitException(
        `exception restarting conversation: ${(e as Error).message}`,
        false,
      ); // Not a 5xx error
    }
  }

  get chatConversationIdKey(): string {
    return `${this.chatClient.channel}_conversationId`;
  }

  get uhConversationIdKey(): string {
    return `${this.uhClient.channel}_conversationId`;
  }

  get chatConversationId(): string | undefined {
    if (this.conversation?.context) {
      return this.conversation?.context[this.chatConversationIdKey] as
        | string
        | undefined;
    }
  }

  get uhConversationId(): string | undefined {
    if (this.conversation?.context) {
      return this.conversation?.context[this.uhConversationIdKey] as
        | string
        | undefined;
    }
  }

  get type(): string {
    return UHLiveChannelsClientType;
  }

  get channel(): ConversationChannel {
    return this.currentChannel;
  }

  get syncStorageMode(): ConversationSyncStorageMode {
    if (this.currentChannel === 'chat') {
      return this.chatClient.syncStorageMode;
    }
    return this.uhClient.syncStorageMode;
  }

  get analyticsParams(): Record<string, unknown> {
    const universalHelpConversationId = this.uhConversationId;
    const audience = this.conversation?.context.audience;
    const userId = this.conversation?.user.id;
    const triggerPoint = this.conversation?.context.triggerParams?.triggerPoint;
    return {
      universalHelpConversationId,
      audience,
      userId,
      triggerPoint,
    };
  }

  disconnect = async (): Promise<void> => {
    if (this.currentChannel === 'chat') {
      return this.chatClient.disconnect();
    }
    return this.uhClient.disconnect();
  };

  action = async (
    params: ConversationActionParams,
  ): Promise<ClientResponse> => {
    try {
      if (!window.navigator.onLine) {
        return this.error(408, 'No network connection');
      }

      if (this.currentChannel === 'chat') {
        // TODO(joesteele): if we're in a live chat and a trigger comes in
        //  handle it, by showing a blocking confirmation modal or something
        //  as we don't want it to blow away their live chat with the agent.
        //  currently, this will just ignore the action, doing nothing.
        //  without this guard though, the chat client would not be able to
        //  handle the action and it would throw an error, aborting the
        //  in-progress chat conversation.
        if (params.action.name === 'handle-trigger-params') {
          return {code: 205};
        }

        return this.chatClient.action({
          ...params,
          conversationId: this.chatConversationId as string,
        });
      }

      const inlineRacAndEmail =
        this.featureFlags.universalHelpUseNewDesign &&
        this.featureFlags.universalHelpUseInlineRacEmailForm;

      const {action, token} = params;

      if (action.name === 'get-smart-suggestions') {
        if (!!this.conversation && !this.conversation.isTabMostRecent()) {
          return {code: 205};
        }

        if (this.conversation?.context.placeholderConversation) {
          return {code: 205};
        }

        if (this.uhClient.isPending() || this.isInlineDataActive()) {
          return {code: 205};
        }

        const latestItem = this.getLatestLogItem();
        let message;
        if (latestItem?.name === 'conversation-message-added') {
          message = latestItem.message as ConversationMessage;
        }
        if (message?.type === 'button-options-conversation-message') {
          // if the latest message was a prompt, replace it by
          // passing the `get-smart-suggestions` as the selected option
          const replaceSuggestionsAction: ConversationAction = {
            name: 'select-option',
            message,
            selectedOptionId: action.name,
            data: action.data,
          };

          return this.action({
            token,
            action: replaceSuggestionsAction,
            conversationId: this.uhConversationId as string,
          });
        }
      }

      const clean = (selected: string) => {
        return selected.replace(/^topic_/, '').replace(/^channel_/, '');
      };

      if (action.name === 'select-option') {
        const {message, selectedOptionId} = action as SelectOptionAction;
        if (message.purpose === 'channel_selection') {
          this.analytics.track('channel_selected', {
            channel: clean(selectedOptionId),
          });
          // intercept RAC + email, setting the intent and requestedChannel if we're not using
          // the inline forms within the Inline Data
          if (selectedOptionId === 'channel_rac') {
            if (inlineRacAndEmail) {
              // so we can trigger the change in scroll behavior
              this.conversation?.setContextValues({
                intent: 'contact_support',
              });
            } else {
              this.conversation?.setContextValues({
                requestedChannel: 'request_a_call',
                intent: 'contact_support',
              });
            }

            // hack: this 205 code is used to signal to the UI
            // that the request was accepted, but not to treat
            // it as the value being set.
            // this was a workaround for intercepting these
            // UI actions to display the modals instead of
            // sending the requests to the server.
            return {code: 205};
          } else if (selectedOptionId === 'channel_email') {
            if (inlineRacAndEmail) {
              // so we can trigger the change in scroll behavior
              this.conversation?.setContextValues({
                intent: 'contact_support',
              });
            } else {
              this.conversation?.setContextValues({
                requestedChannel: 'email',
                intent: 'contact_support',
              });
            }
            // hack: this 205 code is used to signal to the UI
            // that the request was accepted, but not to treat
            // it as the value being set.
            // this was a workaround for intercepting these
            // UI actions to display the modals instead of
            // sending the requests to the server.
            return {code: 205};
          } else if (selectedOptionId === 'channel_chat') {
            this.conversation?.setContextValues({
              requestedChannel: 'chat',
              intent: 'contact_support',
            });
          } else if (selectedOptionId === 'channel_restart') {
            this.conversation?.clearContextValues([
              'intent',
              'requestedChannel',
            ]);
          }
        }

        if (message.purpose === 'topic_selection') {
          this.analytics.track('topic_selected', {
            topic: clean(selectedOptionId),
          });
          this.conversation?.setContextValue(
            'topic',
            selectedOptionId.replace(/^topic_/, ''),
          );
        }

        // Emit an extra analytics event if the user clicked a suggested question
        // with the selected question message for simpler experimentation analysis
        if (selectedOptionId.includes('question_')) {
          const selectedOption = message.options.find(
            (option) => option.id === selectedOptionId,
          );
          this.analytics.track('suggested_question_selected', {
            question: selectedOption?.label,
            optionId: selectedOptionId,
          });
        }
      }

      if (action.name === 'article-visited') {
        const {data} = action;
        const article = data as ConversationArticle;

        let uhContext = this.conversation?.context.universalHelp as
          | UniversalHelpConversationContext
          | undefined;
        if (!uhContext) {
          uhContext = {};
        }
        const articlesVisited =
          (uhContext.articlesVisited as Record<string, ConversationArticle>) ||
          {};
        articlesVisited[article.id] = article;
        uhContext.articlesVisited = articlesVisited;
        this.conversation?.setContextValue('universalHelp', uhContext);
      }

      if (action.name === 'inline-data-result' && !!this.conversation) {
        if (!this.conversation.isTabMostRecent()) {
          this.analytics.track('inactive_tab_inline_data_action_ignored');
          return {code: 205};
        }
      }

      if (action.name === 'inline-data-action' && !!this.conversation) {
        const {data} = action;

        const dataInline = data as Record<string, unknown>;

        if (dataInline && dataInline.purpose === 'channel_selection') {
          const channelID = dataInline.channelID as string;
          this.analytics.track('channel_selected', {
            channel: channelID,
          });
          if (channelID === 'channel_rac') {
            if (inlineRacAndEmail) {
              // so we can trigger the change in scroll behavior
              this.conversation?.setContextValues({
                intent: 'contact_support',
              });
            } else {
              this.conversation?.setContextValues({
                requestedChannel: 'request_a_call',
                intent: 'contact_support',
              });
            }

            return {code: 205};
          } else if (channelID === 'channel_email') {
            if (inlineRacAndEmail) {
              // so we can trigger the change in scroll behavior
              this.conversation?.setContextValues({
                intent: 'contact_support',
              });
            } else {
              this.conversation?.setContextValues({
                requestedChannel: 'email',
                intent: 'contact_support',
              });
            }

            return {code: 205};
          }
        }
      }

      // handle animations complete event
      if (action.name === 'animations-complete') {
        if (
          this.conversation &&
          this.conversation.context.waitingForAnimationsToComplete
        ) {
          this.showChatEndedMessage();
          this.clearCurrentConversation();
        }
        return {code: 200};
      }

      // if something triggered a request to open UH, handle that trigger
      if (action.name === 'handle-trigger-params' && !!this.conversation) {
        // in this case, if we have an ongoing conversation, and the
        // trigger contained a query, send that query as a user message
        const triggerParams = action.triggerParams as TriggerParams;
        const {initialQuery} = triggerParams;
        if (initialQuery) {
          // If an interrupt is *already* being handled, update the message and wait for it to be handled
          if (this.nextTriggerQuery) {
            this.nextTriggerQuery = initialQuery;
            return {code: 205};
          }
          this.nextTriggerQuery = initialQuery;

          // Allow in-flight requests to finish before handling interrupts
          await this.uhClient.waitForPendingMessagesHandled();

          // If there's an ongoing action send an interrupt and wait for the inline data to handle it
          if (this.isInterruptableInlineDataActive()) {
            this.nextTriggerQuery = initialQuery;

            const event = {name: 'inline-data-interruption', nonce: uuid()};
            const result = await this.inlineDataEventBus.emitWithResult(event, {
              timeoutMs: 5_000,
            });

            if (result.error === 'timeout') {
              this.errors.reportWarning(
                'Timed out attempting to interrupt inline data',
                {tags: ERROR_TAGS},
              );
            }
          }

          if (this.nextTriggerQuery) {
            const resp = await this.conversation.sendMessage(
              this.conversation.buildTextMessage(this.nextTriggerQuery),
            );
            this.nextTriggerQuery = undefined;
            return resp;
          } else {
            return {code: 205};
          }
        }
      }

      if (
        this.conversation &&
        shouldConversationSendPageContext(this.conversation)
      ) {
        const result = await this.conversation.action({
          name: 'request-action',
          waitForResponse: true,
          action: {
            name: 'get-page-context',
          },
        });

        if (result.data) {
          action.data = {
            ...(action.data ?? {}),
            ...result.data,
          };
        }
      }

      const prevInputModes = this.conversation?.context.enabledInputModes;

      const resp = await this.uhClient.action({
        token,
        action,
        conversationId: this.uhConversationId as string,
      });

      if (
        resp.code >= 400 &&
        !!this.conversation &&
        !!prevInputModes &&
        !isEqual(this.conversation.context.enabledInputModes, prevInputModes)
      ) {
        // if the message failed, restore the previous input modes
        this.conversation.setContextValue('enabledInputModes', prevInputModes);

        if (
          action.name === 'select-option' &&
          action.selectedOptionId === 'channel_chat'
        ) {
          // if the request failed, reset contact support intent
          this.conversation.clearContextValues(['intent', 'requestedChannel']);
        }
      }

      // this needs to happen after we reset the previous input modes,
      // otherwise it might think it's stuck when it's not
      this.checkForStuckConversation(resp, this.conversation);

      return resp;
    } catch (e) {
      const clientErrorResponse = await this.reportAndEmitException(
        `exception sending conversation action: ${(e as Error).message}`,
      );
      return clientErrorResponse;
    }
  };

  private getLatestLogItem(): ConversationLogItem | null {
    const log = this.conversation?.log.get();

    if (!log || log.length === 0) {
      return null;
    }

    return log[log.length - 1];
  }

  private isActiveInlineDataLogItem(logItem: ConversationLogItem): boolean {
    return (
      logItem.name === 'conversation-message-added' &&
      logItem.message.type === 'inline-data-conversation-message' &&
      !logItem.message.inlineData.isDone
    );
  }

  private isInlineDataActive(): boolean {
    const logItem = this.getLatestLogItem();
    if (!logItem) {
      return false;
    }

    return this.isActiveInlineDataLogItem(logItem);
  }

  private isInterruptableInlineDataActive(): boolean {
    const logItem = this.getLatestLogItem();
    if (!logItem || !this.isActiveInlineDataLogItem(logItem)) {
      return false;
    }

    const {inlineData} = (logItem as ConversationLogItemMessageAdded)
      .message as InlineDataConversationMessage;

    return INTERRUPTABLE_INLINE_DATA_NAMES.includes(inlineData.name);
  }

  private clearCurrentConversation() {
    this.conversationSub?.unsubscribe();
    this.conversationSub = null;
    this.conversation = null;
    this.currentChannel = 'bot';
    this.subscribeToClientStatus(this.uhClient);
  }

  leaveConversation = async (
    params: LeaveConversationParams,
  ): Promise<ClientResponse> => {
    try {
      if (this.currentChannel === 'chat') {
        if (this.conversation) {
          const agentPresent = Object.values(
            this.conversation.participants,
          ).some((p) => p.role === 'agent');

          // If abandoned chat, count messages and cancel task
          if (!agentPresent) {
            this.api
              .abandonChat({
                conversationId: this.chatConversationId as string,
              })
              .then(() => {
                this.incrementAndTrack(METRIC.abandon_chat_success);
              })
              .catch((e) => {
                this.reportAndEmitException(
                  `exception handling abandoned chat: ${(e as Error).message}`,
                  false,
                ); // Not a 5xx error

                this.incrementAndTrack(METRIC.abandon_chat_error);
              });
          }
        }
        const resp = await this.chatClient.leaveConversation({
          ...params,
          conversationId: this.chatConversationId as string,
        });

        setTimeout(() => {
          this.clearCurrentConversation();
        }, 250);

        return resp;
      }

      const {conversationId} = params;

      const resp = await this.uhClient.leaveConversation({
        ...params,
        conversationId: conversationId ?? (this.uhConversationId as string),
      });

      setTimeout(() => {
        this.clearCurrentConversation();
      }, 250);

      return resp;
    } catch (e) {
      const clientErrorResponse = await this.reportAndEmitException(
        `exception leaving conversation: ${(e as Error).message}`,
      );
      return clientErrorResponse;
    }
  };

  getConversationMessages = async (
    params: GetConversationMessagesParams,
  ): Promise<GetConversationMessagesResponse> => {
    if (this.currentChannel === 'chat') {
      return this.chatClient.getConversationMessages({
        ...params,
        conversationId: this.chatConversationId as string,
      });
    }
    return this.uhClient.getConversationMessages({
      ...params,
      conversationId: this.uhConversationId as string,
    });
  };

  getConversationParticipants = async (
    params: GetConversationParticipantsParams,
  ): Promise<GetConversationParticipantsResponse> => {
    if (this.currentChannel === 'chat') {
      return this.chatClient.getConversationParticipants({
        ...params,
        conversationId: this.chatConversationId as string,
      });
    }
    return this.uhClient.getConversationParticipants();
  };

  sendConversationMessage = async (
    params: SendConversationMessageParams,
  ): Promise<ClientResponse> => {
    try {
      const {conversationId, message} = params;
      if (message.type === 'text-conversation-message' && !this.conversation) {
        // if we don't have a conversation,  we're triggering a new conversation
        // from the screen of an ended conversation. emit an event to reload
        this.emitNewConversationEvent({
          name: 'conversation-set-context-values',
          conversationId,
          values: {
            restartConversation: {
              initialQuery: message.text,
            },
          },
        });
        return {code: 205};
      }

      if (message.type === 'text-conversation-message' && !!this.conversation) {
        // Always set the 'latestQuestion' on first message. If it's been identified as a
        // dev or synthetics mode, the 'latestQuestion' will remain as the triggering
        // 'Stripe Internal (Synthetic) Test' message.
        // Otherwise, keep updating the 'latestQuestion` with the newest user message.
        // 'latestQuestion' is then used to prefill email/rac forms, or sent along in chat as context.
        if (
          !this.conversation.context.dev &&
          !this.conversation.context.synthetic
        ) {
          this.conversation.setContextValue('latestQuestion', message.text);
        }

        let swallowMessage = false;
        if (message.text.match(INTERNAL_TEST_STRING)) {
          this.conversation.setContextValues({
            dev: true,
          });
          swallowMessage = true;
        } else if (message.text.match(INTERNAL_SYNTHETIC_TEST_STRING)) {
          this.conversation.setContextValues({
            synthetic: true,
          });
          swallowMessage = true;
        }

        if (swallowMessage) {
          // we'll swallow the message by updating message
          // with a new id marking it to be ignored
          const {id} = message;
          const testId = 'ignore-internal-test-message';

          this.emitNewConversationEvent({
            name: 'conversation-message-updated',
            conversationId,
            participantId: message.participant.id,
            messageId: testId,
            nonce: message.nonce,
            message: {
              ...message,
              id: testId,
            },
          });

          // since the conversation has already added a pending message,
          // we'll also remove that message from the log
          defer(() => {
            if (this.conversation) {
              const log = this.conversation?.log.get();
              this.conversation?.log.set(log.filter((m) => m.id !== id));
            }
          });

          return {code: 200};
        }
      }

      if (this.currentChannel === 'chat') {
        return this.chatClient.sendConversationMessage({
          ...params,
          conversationId: this.chatConversationId as string,
        });
      }

      if (
        this.conversation &&
        shouldConversationSendPageContext(this.conversation)
      ) {
        const result = await this.conversation.action({
          name: 'request-action',
          waitForResponse: true,
          action: {
            name: 'get-page-context',
          },
        });

        if (result.data) {
          message.data = {
            ...message.data,
            ...result.data,
          };
        }
      }

      let prevPromptMessage: ButtonOptionsConversationMessage | undefined;
      if (message.type === 'text-conversation-message') {
        const log = this.conversation?.log.get();

        const lastItem = log && log.length > 0 && log[log.length - 1];
        let lastMessage: ConversationMessage | undefined;

        if (lastItem && lastItem.name === 'conversation-message-added') {
          lastMessage = lastItem.message;
        }
        // depending on the timing, the last message could be this message.
        // I believe this may happen due to the conversation optimistically
        // adding the message in a 'pending' state. when that happens, we
        // look for the previous message before it.
        if (lastMessage?.id === message.id) {
          const prevItem = log && log.length > 1 && log[log.length - 2];
          if (prevItem && prevItem.name === 'conversation-message-added') {
            lastMessage = prevItem.message;
          }
        }

        if (lastMessage?.type === 'button-options-conversation-message') {
          this.emitNewConversationEvent({
            name: 'conversation-message-updated',
            conversationId: this.conversation?.id as string,
            participantId: lastMessage.participant.id,
            messageId: lastMessage.id,
            message: {
              ...prevPromptMessage,
              ...lastMessage,
              selectedOptionId: 'text-input',
            },
          });
        }
      }

      const prevInputModes = this.conversation?.context.enabledInputModes;

      const resp = await this.uhClient.sendConversationMessage({
        ...params,
        conversationId: this.uhConversationId as string,
      });

      if (
        resp.code >= 400 &&
        !!this.conversation &&
        !!prevInputModes &&
        !isEqual(this.conversation.context.enabledInputModes, prevInputModes)
      ) {
        // if the message failed, restore the previous input modes
        this.conversation.setContextValue('enabledInputModes', prevInputModes);
      }

      if (resp.code !== 200 && prevPromptMessage) {
        this.emitNewConversationEvent({
          name: 'conversation-message-updated',
          conversationId: this.conversation?.id as string,
          participantId: prevPromptMessage.participant.id,
          messageId: prevPromptMessage.id,
          message: {
            ...prevPromptMessage,
            selectedOptionId: undefined,
          },
        });
      }

      // this needs to happen after we reset the previous input modes,
      // otherwise it might think it's stuck when it's not
      this.checkForStuckConversation(resp, this.conversation);

      return resp;
    } catch (e) {
      const clientErrorResponse = await this.reportAndEmitException(
        `exception sending conversation message: ${(e as Error).message}`,
      );
      return clientErrorResponse;
    }
  };

  sendConversationTyping = async (
    params: SendConversationTypingParams,
  ): Promise<ClientResponse> => {
    if (this.currentChannel === 'chat') {
      return this.chatClient.sendConversationTyping({
        ...params,
        conversationId: this.chatConversationId as string,
      });
    }
    return this.uhClient.sendConversationTyping();
  };

  async streamText(params: StreamTextParams): Promise<StreamTextResponse> {
    return this.uhClient.streamText(params);
  }

  private buildUHConversationSummaryMessage() {
    if (!this.conversation) {
      throw new Error('No conversation started');
    }

    const lines = ['Transition to agent:'];

    if (this.conversation.context.topic) {
      lines.push(`Selected: ${this.conversation.context.topic}`);
    }

    if (this.conversation.context.latestQuestion) {
      lines.push(
        `\nUser message:\n${this.conversation.context.latestQuestion}`,
      );
    } else {
      const firstSelectionItem = this.conversation.log.get().find((item) => {
        return (
          item.name === 'conversation-message-added' &&
          item.message.type === 'button-options-conversation-message'
        );
      }) as ConversationLogItemMessageAdded | undefined;

      const msg = firstSelectionItem?.message as
        | ButtonOptionsConversationMessage
        | undefined;
      const firstSelection = msg?.options.find(
        (option) => option.id === msg?.selectedOptionId,
      );

      if (firstSelection) {
        lines.push(`\nUser message:\n${firstSelection.label}`);
      }
    }

    return this.conversation.buildTextMessage(lines.join('\n'));
  }

  private subscribeToClientStatus(client: Client) {
    this.clientStatusSub?.unsubscribe();
    this.status$.next(client.getStatus());
    this.clientStatusSub = client
      .statusObservable()
      // debounce will also defer updating status from the child client
      // to avoid our base class overwriting the connection status
      .pipe(debounceTime(50))
      .subscribe((s) => {
        this.status$.next(s);
      });
  }

  private incrementAndTrack(
    name: string,
    params: Record<string, string | number> = {},
  ): void {
    const metricParams = {...METRIC_TAGS, ...params};
    this.metrics.increment(name, metricParams);
    this.analytics.track(name, metricParams);
  }

  protected startPlaceholderConversation(
    params: StartConversationParams & {
      conversationId?: string;
      channelConversationId?: string;
      participant: ConversationParticipant;
    },
  ): StartConversationResponse {
    const {participant} = params;

    const {context} = params;
    if (!context?.triggerParams) {
      throw new Error(
        'Error starting placeholder conversation - missing triggerParams',
      );
    }

    const {initialQuery, initialGreeting} = context.triggerParams;
    const timestamp = Date.now();

    const tempMessages: Record<string, ConversationMessage> = {};
    if (initialGreeting) {
      tempMessages['inital-greeting'] = {
        id: 'initial-greeting',
        nonce: 'initial-greeting',
        type: 'text-conversation-message',
        timestamp: timestamp - 200, // we want this to be first in the log
        participant: UniversalHelpUser,
        text: initialGreeting,
      };
    }

    if (initialQuery) {
      tempMessages['initial-query'] = {
        id: 'initial-query',
        nonce: 'initial-query',
        type: 'text-conversation-message',
        timestamp: timestamp + 100,
        participant,
        text: initialQuery as string,
      };
    }

    const conversation = new Conversation(this, {
      id: uuid(),
      user: participant,
      status: ConversationStatus.Active,
      syncStorage: false,
      messages: tempMessages,
      participants: {
        [UniversalHelpUser.id]: UniversalHelpUser,
        [participant.id]: participant,
      },
      context: {
        ...context,
        channel: 'bot',
        enabledInputModes: [],
      },
    });

    // Provide a "loading" state for the placeholder conversation
    conversation.handleEventFromClient({
      id: uuid(),
      name: 'conversation-typing-started',
      conversationId: conversation.id,
      participantId: UniversalHelpUser.id,
      timestamp: Date.now(),
    });

    return {code: 200, data: {conversation}};
  }

  private async reportAndEmitException(
    error: string,
    is5xxErrorCode = true,
  ): Promise<ClientErrorResponse> {
    let clientErrorResponse: ClientErrorResponse;
    const errorTags = {
      ...ERROR_TAGS,
      assistant_error_screen: true,
    };
    if (is5xxErrorCode) {
      clientErrorResponse = await this.error(500, error, errorTags);
    } else {
      const eventId = this.errors.reportError(error, {
        tags: errorTags,
      });

      clientErrorResponse = {
        // this code value satisfies the return type, however, currently all instances of non 5xx errors
        // do not use this return value, they just fire-and-forget.
        code: 400,
        error,
        sentryEventId: eventId,
      };
    }

    this.emitNewEvent({
      name: 'client-exception',
      error,
      sentryEventId: clientErrorResponse.sentryEventId,
    });

    return clientErrorResponse;
  }

  public hasShownInitialGreeting(): boolean {
    return this.initialGreetingShown;
  }

  public markInitialGreetingAsShown(): void {
    this.initialGreetingShown = true;
  }

  private inProgressResponseForRequest(
    responses: InProgressResponse[],
    params: InProgressParams,
  ): InProgressResponse {
    let channelInProgressResponse: InProgressResponse | undefined;
    let chatInProgressResponse: InProgressResponse | undefined;
    let racInProgressResponse: InProgressResponse | undefined;
    const actuallyInProgressResponses = responses.filter((resp) => {
      if ('error' in resp) {
        return false;
      }

      if (
        params.context?.channel &&
        resp.data.channel === params.context?.channel
      ) {
        channelInProgressResponse = resp;
      }

      // only set if is actually in progress, so we can prefer it
      if (resp.data.channel === 'chat' && resp.data.inProgress) {
        chatInProgressResponse = resp;
      }

      // only set if is actually in progress, so we can prefer it
      if (resp.data.channel === 'request_a_call' && resp.data.inProgress) {
        racInProgressResponse = resp;
      }

      return resp.data.inProgress;
    });

    // if the request is asking for a specific channel, prefer it
    if (channelInProgressResponse) {
      return channelInProgressResponse;
    }

    // else if we have an ongoing chat, prefer it regardless of other in progress calls
    if (chatInProgressResponse) {
      return chatInProgressResponse;
    }

    // else if we have an ongoing request a call, prefer it over the bot
    if (racInProgressResponse) {
      return racInProgressResponse;
    }

    if (actuallyInProgressResponses.length > 0) {
      return actuallyInProgressResponses[0];
    }

    return {code: 200, data: {inProgress: false}};
  }

  checkForStuckConversation(
    resp: ClientResponse,
    conversation: Conversation | undefined | null,
  ): void {
    if (resp.code === 400 && !!conversation) {
      const failedRequests =
        this.incrementFailedConversationRequests(conversation);

      if (this.conversationLooksStuck(failedRequests, conversation)) {
        // now, we don't want to unnecessarily halt the conversation
        // with an error screen. for now, let's just get visibility
        this.errors.reportWarning('Conversation looks stuck', {
          tags: ERROR_TAGS,
          extras: {
            conversation_id: conversation.id,
          },
        });
      }
    } else {
      this.clearFailedConversationRequests(conversation);
    }
  }

  private incrementFailedConversationRequests(
    conversation: Conversation,
  ): number {
    const count = this.stuckConversationRequests.get(conversation) ?? 0;
    const newCount = count + 1;
    this.stuckConversationRequests.set(conversation, newCount);
    return newCount;
  }

  private clearFailedConversationRequests(
    conversation: Conversation | undefined | null,
  ) {
    if (conversation) {
      this.stuckConversationRequests.delete(conversation);
    }
  }

  private conversationLooksStuck(
    failedRequests: number,
    conversation: Conversation,
  ): boolean {
    const hasPrompt = conversation.log.get().some((item) => {
      if (item.name !== 'conversation-message-added') {
        return false;
      }
      if (
        ![
          'button-options-conversation-message',
          'select-option-conversation-message',
        ].includes(item.message.type)
      ) {
        return false;
      }

      const message = item.message as
        | ButtonOptionsConversationMessage
        | SelectOptionsConversationMessage;

      // if there's an option message (prompt) with an unselected
      // option, the user can use it to select an option and
      // send a message
      return message.selectedOptionId === undefined;
    });

    if (
      failedRequests > 0 &&
      this.conversationInputIsDisabled(conversation) &&
      !hasPrompt
    ) {
      // if we don't have a prompt and the input is also disabled, that would mean
      // the conversation is probably stuck as the user doesn't have a way of sending
      // a message.
      return true;
    }

    return false;
  }

  private conversationInputIsDisabled(conversation: Conversation): boolean {
    const inputModes = (conversation.context.enabledInputModes ||
      []) as string[];
    const inputEnabled = inputModes.includes('text');
    return !inputEnabled;
  }

  sendFeedback({
    conversationId,
    responseId,
    feedback,
  }: SendFeedbackParams): Promise<ClientResponse> {
    return this.uhClient.sendFeedback({
      conversationId,
      responseId,
      feedback,
    });
  }
}
